import { c as _c } from "react/compiler-runtime";
import { queryClient } from "@/config/react-query";
import { useWebSocketCallbackSubscription } from "@/hooks/use-websocket-subscription";
import { isResultsPhase } from "@/lib/lab-status";
import type { AlertDetail, LabOrderUpdatedData } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";
import { type LabInternalStatus, type LabOrder, type LabOrderListItem, type LabOrderStatus, type LabVendor, isLabVendor } from "@/types/lab";
import { labOrderListKeyPrefix, labPatientOrderPagesKeyPrefix, labQueryKeys, setLabOrderViewedAtInCaches, setLabResultsReadyCountInCache } from "@/api/provider/lab/lab-query";
import { measurementQueryKeys } from "@/query/patient/measurements";
import { measurementFieldQueryKeys } from "@/query/settings/measurement-fields";
import { measurementGroupQueryKeys } from "@/query/settings/measurement-groups";

// Coalesce results-ready-count refetches. Fullscript syncs publish one
// LAB_ORDER_UPDATED per order in bursts, which would otherwise refetch the
// count once per message. Debouncing collapses the burst into a single
// refetch on the trailing edge.
let resultsReadyCountInvalidateTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleResultsReadyCountInvalidation() {
  if (resultsReadyCountInvalidateTimer) clearTimeout(resultsReadyCountInvalidateTimer);
  resultsReadyCountInvalidateTimer = setTimeout(() => {
    resultsReadyCountInvalidateTimer = null;
    queryClient.invalidateQueries({
      queryKey: labQueryKeys.resultsReadyCount()
    });
  }, 500);
}

/**
 * Subscribe to lab-order websocket events and keep the patient's lab order
 * list and the open order detail in sync in React Query cache.
 *
 * Mirrors the surgical-cache pattern the other realtime hooks use (e.g.
 * usePatientRealtimeCache): patch the cached row/detail directly from the
 * payload rather than refetching. Two cases still fall back to a refetch:
 *   - the order isn't in the cached list yet (a brand-new order-created) or the
 *     list was never loaded — refetch so the new row appears;
 *   - the order just completed — the server has fetched biomarker results that
 *     aren't carried in the payload, so refetch the detail to pull them in.
 */
export function useLabRealtimeCache() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = function handleMessage(message) {
      if (message.type !== AlertType.LAB_ORDER_UPDATED) {
        return;
      }
      const data = message.data as LabOrderUpdatedData | undefined;
      if (!data?.patient_id || !data.lab_order_id) {
        return;
      }
      const internalStatus = data.internal_status as LabInternalStatus;
      const vendorStatus = (data.vendor_status ?? undefined) as LabOrderStatus | undefined;
      queryClient.invalidateQueries({
        queryKey: labOrderListKeyPrefix
      });
      queryClient.invalidateQueries({
        queryKey: [...labPatientOrderPagesKeyPrefix, data.patient_id]
      });
      if (typeof data.results_ready_count === "number") {
        setLabResultsReadyCountInCache(data.results_ready_count);
      } else {
        scheduleResultsReadyCountInvalidation();
      }
      if (data.event === "results_viewed") {
        const resultsViewedAt = data.results_viewed_at ?? undefined;
        setLabOrderViewedAtInCaches(data.patient_id, data.lab_order_id, resultsViewedAt);
        queryClient.setQueryData(labQueryKeys.order(data.lab_order_id), prev => prev ? {
          ...prev,
          resultsViewedAt
        } : prev);
        return;
      }
      const vendor = isLabVendor(data.vendor) ? data.vendor : undefined;
      const unreadableVendor = data.vendor != null && vendor === undefined;
      const isCompleted = !unreadableVendor && isResultsPhase(vendorStatus, vendor);
      const listKey = labQueryKeys.orders(data.patient_id);
      const cachedList = queryClient.getQueryData(listKey);
      if (cachedList?.some(o_0 => o_0.id === data.lab_order_id) && !isCompleted && !unreadableVendor) {
        queryClient.setQueryData(listKey, prev_0 => prev_0?.map(o => o.id === data.lab_order_id ? {
          ...o,
          internalStatus,
          vendorStatus,
          vendorUpdatedAt: data.vendor_updated_at ?? o.vendorUpdatedAt
        } : o));
      } else {
        queryClient.invalidateQueries({
          queryKey: listKey
        });
      }
      const detailKey = labQueryKeys.order(data.lab_order_id);
      if (isCompleted) {
        queryClient.invalidateQueries({
          queryKey: detailKey
        });
        queryClient.invalidateQueries({
          queryKey: labQueryKeys.biomarkers(data.patient_id)
        });
        queryClient.invalidateQueries({
          queryKey: measurementQueryKeys.patient(data.patient_id)
        });
        queryClient.invalidateQueries({
          queryKey: measurementFieldQueryKeys.list()
        });
        queryClient.invalidateQueries({
          queryKey: measurementGroupQueryKeys.list()
        });
      } else {
        if (unreadableVendor) {
          queryClient.invalidateQueries({
            queryKey: detailKey
          });
        } else {
          queryClient.setQueryData(detailKey, prev_1 => prev_1 ? {
            ...prev_1,
            internalStatus,
            vendorStatus
          } : prev_1);
        }
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const handleMessage = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [AlertType.LAB_ORDER_UPDATED];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useWebSocketCallbackSubscription(t1, handleMessage, true, "useLabRealtimeCache");
}